// OrganizacoesView — gestão de "companies" (renomeadas como Organizações na UI).
//
// Funcionalidades:
//   - Lista com busca + filtro (todas / ativas / arquivadas)
//   - KPIs: total, ativas, autônomas, com 0 admins
//   - Modal de criação (instituição OU profissional autônomo)
//     * Admin obrigatório: pick user existente OU criar inline (email, nome, senha)
//   - Modal de edição (nome, slug, settings, arquivar/restaurar)
//   - Modal "Ver membros" agrupados por role
//
// Toda organização precisa de ao menos 1 admin (validado no endpoint).

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  Building2,
  CheckCircle2,
  GraduationCap,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  Shield,
  User as UserIcon,
  UserPlus,
  Users,
  UsersRound,
  X
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { Switch } from "@app/ui/components/switch";
import { openAlert } from "./ConfirmPromptModal";
import {
  api,
  ApiError,
  type CompanyDetail,
  type CompanyListRow,
  type UserRow
} from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";

const companyList = signal<CompanyListRow[] | null>(null);
const allUsers = signal<UserRow[] | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const filterStatus = signal<"all" | "active" | "archived">("active");
const editing = signal<CompanyDetail | null>(null);
const creating = signal<boolean>(false);
const viewing = signal<CompanyDetail | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const [cres, ures] = await Promise.all([api.listCompanies(), api.listUsers()]);
    companyList.value = cres.companies;
    allUsers.value = ures.users;
    phase.value = "ready";
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(err);
    phase.value = "error";
  }
}

async function openEdit(id: string) {
  try {
    const res = await api.getCompany(id);
    editing.value = res.company;
  } catch (err) {
    void openAlert({ title: "Erro", message: friendlyError(err) });
  }
}

async function openView(id: string) {
  try {
    const res = await api.getCompany(id);
    viewing.value = res.company;
  } catch (err) {
    void openAlert({ title: "Erro", message: friendlyError(err) });
  }
}

export function OrganizacoesView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading") return <CompaniesLoading />;
  if (phase.value === "error") return <CompaniesError />;
  return (
    <>
      <CompaniesReady />
      {editing.value && <EditCompanyModal company={editing.value} />}
      {viewing.value && <ViewMembersModal company={viewing.value} />}
      {creating.value && <CreateCompanyModal />}
    </>
  );
}

function CompaniesLoading() {
  return (
    <div class="space-y-3">
      <Skeleton className="h-12 rounded-md" />
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-16 rounded-md" />
      ))}
    </div>
  );
}

function CompaniesError() {
  return (
    <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
      <div class="flex items-start gap-2">
        <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
        <div>
          <h3 class="text-sm font-bold">Falha ao listar organizações</h3>
          {errMsg.value && <p class="text-xs text-muted-foreground">{errMsg.value}</p>}
          <button
            type="button"
            onClick={() => load()}
            class="mt-2 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    </div>
  );
}

function CompaniesReady() {
  const rows = companyList.value ?? [];
  const q = search.value.toLowerCase().trim();
  const fs = filterStatus.value;
  const visible = useMemo(() => {
    return rows.filter((c) => {
      if (fs === "active" && c.archivedAt) return false;
      if (fs === "archived" && !c.archivedAt) return false;
      if (q) {
        const hay = `${c.name} ${c.slug} ${c.autonomousUserEmail ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, q, fs]);

  const counts = useMemo(() => {
    let active = 0,
      archived = 0,
      autonomous = 0,
      noAdmin = 0;
    for (const c of rows) {
      if (c.archivedAt) archived++;
      else active++;
      if (c.isAutonomous) autonomous++;
      if (c.adminCount === 0) noAdmin++;
    }
    return { total: rows.length, active, archived, autonomous, noAdmin };
  }, [rows]);

  return (
    <div class="space-y-4">
      {/* Setting de default órfãos — admin escolhe pra onde vão alunos
          sem outra organização. [[project-admin-default-orphan-org]] */}
      <OrphanDefaultPicker />

      {/* KPI strip */}
      <div class="grid gap-2 sm:grid-cols-4">
        <Kpi label="Total" value={counts.total} icon={Building2} />
        <Kpi label="Ativas" value={counts.active} icon={CheckCircle2} accent="emerald" />
        <Kpi label="Autônomas" value={counts.autonomous} icon={UserIcon} />
        <Kpi
          label="Sem admin"
          value={counts.noAdmin}
          icon={AlertCircle}
          accent={counts.noAdmin > 0 ? "rose" : undefined}
        />
      </div>

      {/* Toolbar */}
      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            placeholder="Buscar nome, slug, email do autônomo…"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
        <StatusPill
          label="Ativas"
          count={counts.active}
          active={filterStatus.value === "active"}
          onClick={() => (filterStatus.value = "active")}
        />
        <StatusPill
          label="Arquivadas"
          count={counts.archived}
          active={filterStatus.value === "archived"}
          onClick={() => (filterStatus.value = "archived")}
        />
        <StatusPill
          label="Todas"
          count={counts.total}
          active={filterStatus.value === "all"}
          onClick={() => (filterStatus.value = "all")}
        />
        <button
          type="button"
          onClick={() => (creating.value = true)}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
        >
          <Plus width="1.1em" height="1.1em" />
          Nova organização
        </button>
      </div>

      <p class="px-1 text-xs text-muted-foreground">
        {visible.length} de {rows.length} organizaç{rows.length !== 1 ? "ões" : "ão"}
      </p>

      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhuma organização encontrada.
        </div>
      ) : (
        <div class="overflow-hidden rounded-md border border-border bg-card/40">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <th class="px-3 py-2">Organização</th>
                <th class="px-3 py-2">Tipo</th>
                <th class="px-3 py-2">Admins</th>
                <th class="px-3 py-2">Profs</th>
                <th class="px-3 py-2">Alunos</th>
                <th class="px-3 py-2">Status</th>
                <th class="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <CompanyListRowEl key={c.id} company={c} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  icon: Icon,
  accent
}: {
  label: string;
  value: number;
  icon: typeof Building2;
  accent?: "emerald" | "rose";
}) {
  const ring =
    accent === "emerald"
      ? "ring-emerald-500/30 text-emerald-700 dark:text-emerald-300"
      : accent === "rose"
      ? "ring-rose-500/30 text-rose-700 dark:text-rose-300"
      : "ring-border text-foreground";
  return (
    <div class={`flex items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2 ring-1 ${ring}`}>
      <Icon width="1.1em" height="1.1em" className="shrink-0" />
      <div class="min-w-0">
        <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div class="text-base font-bold tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function StatusPill({
  label,
  count,
  active,
  onClick
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${
        active
          ? "border-primary/50 bg-primary/10 text-primary"
          : "border-border bg-background/50 text-muted-foreground hover:bg-accent"
      }`}
    >
      {label}
      <span class="rounded bg-accent/60 px-1 text-[10px] tabular-nums opacity-80">
        {count}
      </span>
    </button>
  );
}

function CompanyListRowEl({ company }: { company: CompanyListRow }) {
  const archived = !!company.archivedAt;
  return (
    <tr
      class={`border-b border-border/60 transition-colors last:border-0 hover:bg-accent/30 ${
        archived ? "opacity-60" : ""
      }`}
    >
      <td class="px-3 py-2">
        <div class="font-semibold">{company.name}</div>
        <div class="font-mono text-[11px] text-muted-foreground">{company.slug}</div>
      </td>
      <td class="px-3 py-2">
        {company.isAutonomous ? (
          <span class="inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 ring-1 ring-amber-500/30 dark:text-amber-300">
            <UserIcon width="0.9em" height="0.9em" />
            Autônomo
          </span>
        ) : (
          <span class="inline-flex items-center gap-1 rounded bg-blue-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-700 ring-1 ring-blue-500/30 dark:text-blue-300">
            <Building2 width="0.9em" height="0.9em" />
            Instituição
          </span>
        )}
      </td>
      <td class="px-3 py-2">
        <CountPill
          n={company.adminCount}
          icon={Shield}
          tone={company.adminCount === 0 ? "warn" : "neutral"}
        />
      </td>
      <td class="px-3 py-2">
        <CountPill n={company.teacherCount} icon={GraduationCap} tone="neutral" />
      </td>
      <td class="px-3 py-2">
        <CountPill n={company.studentCount} icon={Users} tone="neutral" />
      </td>
      <td class="px-3 py-2">
        {archived ? (
          <span class="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Archive width="0.9em" height="0.9em" />
            Arquivada
          </span>
        ) : (
          <span class="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-500/30 dark:text-emerald-300">
            <CheckCircle2 width="0.9em" height="0.9em" />
            Ativa
          </span>
        )}
      </td>
      <td class="px-3 py-2 text-right">
        <div class="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => openView(company.id)}
            class="rounded-md border border-border bg-background p-1.5 text-xs hover:bg-accent"
            title="Ver membros"
          >
            <UsersRound width="0.9em" height="0.9em" />
          </button>
          <button
            type="button"
            onClick={() => openEdit(company.id)}
            class="rounded-md border border-border bg-background p-1.5 text-xs hover:bg-accent"
            title="Editar organização"
          >
            <Pencil width="0.9em" height="0.9em" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function CountPill({
  n,
  icon: Icon,
  tone
}: {
  n: number;
  icon: typeof Shield;
  tone: "neutral" | "warn";
}) {
  const cls =
    tone === "warn" && n === 0
      ? "bg-rose-500/15 text-rose-700 ring-1 ring-rose-500/30 dark:text-rose-300"
      : "bg-accent/50";
  return (
    <span
      class={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-bold tabular-nums ${cls}`}
    >
      <Icon width="0.9em" height="0.9em" />
      {n}
    </span>
  );
}

// ---------- CreateCompanyModal ----------

function CreateCompanyModal() {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [isAutonomous, setIsAutonomous] = useState(false);
  const [adminMode, setAdminMode] = useState<"new" | "existing">("new");
  const [adminUserId, setAdminUserId] = useState<string>("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [showClassAverages, setShowClassAverages] = useState(true);
  const [studentsSeeOtherStudents, setStudentsSeeOtherStudents] = useState(false);
  // Pix opcional — só faz sentido pra autônomo (organização institucional
  // configura via área da company depois).
  const [pixKey, setPixKey] = useState("");
  const [pixKeyType, setPixKeyType] = useState<
    "email" | "cpf" | "cnpj" | "phone" | "random"
  >("email");
  const [pixRecipientName, setPixRecipientName] = useState("");
  const [pixCity, setPixCity] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Auto-gera slug a partir do nome até o usuário tocar no campo manualmente.
  // Para autônomos, prefixa "autonomo-" para identificar no banco/admin.
  function computeSlug(nm: string, autonomous: boolean): string {
    const base = nm
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return autonomous && base ? `autonomo-${base}` : base;
  }
  function syncSlugFromName(newName: string) {
    setName(newName);
    if (!slugTouched) setSlug(computeSlug(newName, isAutonomous));
  }
  // Quando alterna instituição ↔ autônomo, atualiza o slug auto-gerado para
  // adicionar/remover o prefixo "autonomo-".
  useEffect(() => {
    if (!slugTouched) setSlug(computeSlug(name, isAutonomous));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutonomous]);

  async function submit() {
    if (!name.trim()) {
      setErr("Nome obrigatório.");
      return;
    }
    if (adminMode === "existing" && !adminUserId) {
      setErr("Selecione o usuário admin.");
      return;
    }
    if (adminMode === "new") {
      if (!adminEmail.trim() || !adminEmail.includes("@")) {
        setErr("Email do admin inválido.");
        return;
      }
      if (!adminName.trim()) {
        setErr("Nome do admin obrigatório.");
        return;
      }
      if (adminPassword.length < 8) {
        setErr("Senha do admin precisa de ao menos 8 caracteres.");
        return;
      }
    }
    setSaving(true);
    setErr(null);
    // Padrão fechado pra TODA organização autônoma: o nome é sempre
    // "{Nome do professor} · Autônomo". Frontend faz o append antes de
    // mandar pro backend pra a regra ser visível no preview.
    const finalName = isAutonomous
      ? `${name.trim()} · Autônomo`
      : name.trim();
    try {
      await api.createCompany({
        name: finalName,
        slug: slug.trim() || undefined,
        isAutonomous,
        settings: {
          showClassAverages,
          studentsSeeOtherStudents
        },
        adminUserId: adminMode === "existing" ? adminUserId : undefined,
        adminUser:
          adminMode === "new"
            ? {
                email: adminEmail.trim().toLowerCase(),
                name: adminName.trim(),
                password: adminPassword
              }
            : undefined,
        // Pix só se preencheu
        pixKey: pixKey.trim() || null,
        pixKeyType: pixKey.trim() ? pixKeyType : null,
        pixRecipientName: pixRecipientName.trim() || null,
        pixCity: pixCity.trim() || null
      });
      creating.value = false;
      await load();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title="Nova organização" onClose={() => (creating.value = false)}>
      <div class="space-y-4 p-5">
        <div>
          <span class="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Tipo
          </span>
          <div class="grid grid-cols-2 gap-2">
            <TypeOption
              icon={Building2}
              label="Instituição"
              desc="Empresa/escola com vários usuários"
              active={!isAutonomous}
              onClick={() => setIsAutonomous(false)}
            />
            <TypeOption
              icon={UserIcon}
              label="Profissional autônomo"
              desc="1 usuário só, ele é o admin"
              active={isAutonomous}
              onClick={() => setIsAutonomous(true)}
            />
          </div>
        </div>

        <Field label={isAutonomous ? "Nome do professor *" : "Nome *"}>
          <input
            type="text"
            value={name}
            onInput={(e) => syncSlugFromName((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            placeholder={isAutonomous ? "ex: Luis Moraes" : "ex: UNICID"}
            autoFocus
          />
          {isAutonomous && name.trim() && (
            <p class="mt-1 text-[11px] text-muted-foreground">
              Será criada como{" "}
              <strong class="text-foreground">
                "{name.trim()} · Autônomo"
              </strong>{" "}
              — padrão para todos os autônomos.
            </p>
          )}
        </Field>
        <Field label="Slug">
          <input
            type="text"
            value={slug}
            onInput={(e) => {
              setSlug((e.target as HTMLInputElement).value);
              setSlugTouched(true);
            }}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm font-mono outline-none focus:border-primary"
            placeholder="auto"
          />
          <p class="mt-1 text-[10px] text-muted-foreground">
            Identificador URL-safe. Auto-gerado a partir do nome.
          </p>
        </Field>

        <div class="rounded-md border border-violet-500/30 bg-violet-500/5 p-3">
          <div class="mb-2 flex items-center gap-1.5">
            <Shield width="1em" height="1em" className="text-violet-600 dark:text-violet-300" />
            <span class="text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
              Admin da organização (obrigatório)
            </span>
          </div>
          <div class="mb-2 grid grid-cols-2 gap-2 text-[11px]">
            <button
              type="button"
              onClick={() => setAdminMode("new")}
              class={`rounded-md border px-2 py-1 font-semibold ${
                adminMode === "new"
                  ? "border-primary/50 bg-primary/10 text-primary"
                  : "border-border bg-background text-muted-foreground hover:bg-accent"
              }`}
            >
              Criar usuário novo
            </button>
            <button
              type="button"
              onClick={() => setAdminMode("existing")}
              class={`rounded-md border px-2 py-1 font-semibold ${
                adminMode === "existing"
                  ? "border-primary/50 bg-primary/10 text-primary"
                  : "border-border bg-background text-muted-foreground hover:bg-accent"
              }`}
            >
              Usar usuário existente
            </button>
          </div>
          {adminMode === "new" ? (
            <div class="space-y-2">
              <input
                type="email"
                value={adminEmail}
                onInput={(e) => setAdminEmail((e.target as HTMLInputElement).value)}
                placeholder="email do admin"
                class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
              />
              <input
                type="text"
                value={adminName}
                onInput={(e) => setAdminName((e.target as HTMLInputElement).value)}
                placeholder="nome completo"
                class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
              />
              <input
                type="password"
                value={adminPassword}
                onInput={(e) => setAdminPassword((e.target as HTMLInputElement).value)}
                placeholder="senha inicial (mín 8 chars — será forçada troca no 1º login)"
                class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
              />
            </div>
          ) : (
            <UserPicker selected={adminUserId} onChange={setAdminUserId} />
          )}
        </div>

        {/* Pix da organização — opcional na criação. Aparece sempre, mas é
            obrigatório pra QR Pix funcionar (compras_pacote). */}
        <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
          <div class="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
            Pix da organização (opcional)
          </div>
          <p class="mb-3 text-[11px] text-muted-foreground">
            Necessário pra gerar QR Code de cobrança nos pacotes. Pode preencher
            depois nas configurações da organização.
          </p>
          <div class="grid grid-cols-2 gap-2">
            <Field label="Tipo de chave">
              <select
                value={pixKeyType}
                onChange={(e) =>
                  setPixKeyType(
                    (e.target as HTMLSelectElement).value as
                      | "email"
                      | "cpf"
                      | "cnpj"
                      | "phone"
                      | "random"
                  )
                }
                title="Tipo da chave Pix"
                aria-label="Tipo da chave Pix"
                class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
              >
                <option value="email">Email</option>
                <option value="cpf">CPF</option>
                <option value="cnpj">CNPJ</option>
                <option value="phone">Celular</option>
                <option value="random">Chave aleatória</option>
              </select>
            </Field>
            <Field label="Chave Pix">
              <input
                type="text"
                value={pixKey}
                onInput={(e) => setPixKey((e.target as HTMLInputElement).value)}
                class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm font-mono outline-none focus:border-primary"
                placeholder="ex: prof@email.com"
              />
            </Field>
          </div>
          <div class="mt-2 grid grid-cols-2 gap-2">
            <Field label="Nome do recebedor (máx 25)">
              <input
                type="text"
                value={pixRecipientName}
                onInput={(e) =>
                  setPixRecipientName((e.target as HTMLInputElement).value)
                }
                maxLength={25}
                class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                placeholder="ex: Luis Moraes"
              />
            </Field>
            <Field label="Cidade (máx 15)">
              <input
                type="text"
                value={pixCity}
                onInput={(e) => setPixCity((e.target as HTMLInputElement).value)}
                maxLength={15}
                class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                placeholder="ex: SAO PAULO"
              />
            </Field>
          </div>
        </div>

        <div>
          <span class="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Configurações
          </span>
          <label class="flex items-center gap-2 text-xs">
            <Switch
              checked={showClassAverages}
              onCheckedChange={(v) => setShowClassAverages(v)}
            />
            Mostrar médias da turma aos alunos
          </label>
          <label class="mt-1 flex items-center gap-2 text-xs">
            <Switch
              checked={studentsSeeOtherStudents}
              onCheckedChange={(v) => setStudentsSeeOtherStudents(v)}
            />
            Alunos podem ver outros alunos
          </label>
        </div>

        {err && (
          <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {err}
          </div>
        )}
      </div>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (creating.value = false)}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          disabled={saving}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={saving || !name.trim()}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 width="1.1em" height="1.1em" className="animate-spin" />
          ) : (
            <UserPlus width="1.1em" height="1.1em" />
          )}
          Criar organização
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

function TypeOption({
  icon: Icon,
  label,
  desc,
  active,
  onClick
}: {
  icon: typeof Building2;
  label: string;
  desc: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`flex items-start gap-2 rounded-md border p-3 text-left text-xs transition-colors ${
        active
          ? "border-primary/50 bg-primary/5"
          : "border-border bg-background hover:bg-accent/40"
      }`}
    >
      <Icon width="1.3em" height="1.3em" className={active ? "text-primary" : "text-muted-foreground"} />
      <div class="min-w-0 flex-1">
        <div class="font-bold">{label}</div>
        <div class="text-[10px] text-muted-foreground">{desc}</div>
      </div>
    </button>
  );
}

function UserPicker({
  selected,
  onChange
}: {
  selected: string;
  onChange: (id: string) => void;
}) {
  const [q, setQ] = useState("");
  const users = allUsers.value ?? [];
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return users.slice(0, 20);
    return users
      .filter((u) => `${u.email} ${u.name}`.toLowerCase().includes(t))
      .slice(0, 20);
  }, [users, q]);
  const selUser = users.find((u) => u.id === selected);
  return (
    <div class="space-y-2">
      <input
        type="search"
        value={q}
        onInput={(e) => setQ((e.target as HTMLInputElement).value)}
        placeholder="buscar usuário…"
        class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
      />
      <div class="max-h-[200px] overflow-y-auto rounded-md border border-border bg-background">
        {filtered.length === 0 ? (
          <p class="p-3 text-center text-xs italic text-muted-foreground">
            Nenhum usuário encontrado.
          </p>
        ) : (
          <ul class="divide-y divide-border">
            {filtered.map((u) => (
              <li
                key={u.id}
                class={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
                  selected === u.id ? "bg-primary/10" : "hover:bg-accent/40"
                }`}
                onClick={() => onChange(u.id)}
              >
                <input
                  type="radio"
                  checked={selected === u.id}
                  readOnly
                  aria-label={`Selecionar ${u.email}`}
                  class="size-3.5"
                />
                <div class="min-w-0 flex-1">
                  <div class="truncate font-semibold">{u.name}</div>
                  <div class="truncate font-mono text-[10px] text-muted-foreground">
                    {u.email}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {selUser && (
        <p class="text-[11px] text-muted-foreground">
          Selecionado: <span class="font-semibold">{selUser.name}</span> ({selUser.email})
        </p>
      )}
    </div>
  );
}

// ---------- EditCompanyModal ----------

function EditCompanyModal({ company }: { company: CompanyDetail }) {
  const [name, setName] = useState(company.name);
  const [slug, setSlug] = useState(company.slug);
  const [showClassAverages, setShowClassAverages] = useState(
    (company.settings?.showClassAverages as boolean | undefined) ?? true
  );
  const [studentsSeeOtherStudents, setStudentsSeeOtherStudents] = useState(
    (company.settings?.studentsSeeOtherStudents as boolean | undefined) ?? false
  );
  const [archived, setArchived] = useState(!!company.archivedAt);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!name.trim()) {
      setErr("Nome obrigatório.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await api.patchCompany(company.id, {
        name: name.trim(),
        slug: slug.trim() || undefined,
        settings: { showClassAverages, studentsSeeOtherStudents },
        archived
      });
      editing.value = null;
      await load();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title={`Editar: ${company.name}`} onClose={() => (editing.value = null)}>
      <div class="space-y-4 p-5">
        <Field label="Nome *">
          <input
            type="text"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            placeholder="Nome da organização"
            aria-label="Nome da organização"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
        <Field label="Slug">
          <input
            type="text"
            value={slug}
            onInput={(e) => setSlug((e.target as HTMLInputElement).value)}
            placeholder="slug-da-organizacao"
            aria-label="Slug da organização"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm font-mono outline-none focus:border-primary"
          />
        </Field>
        <div>
          <span class="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Configurações
          </span>
          <label class="flex items-center gap-2 text-xs">
            <Switch
              checked={showClassAverages}
              onCheckedChange={(v) => setShowClassAverages(v)}
            />
            Mostrar médias da turma aos alunos
          </label>
          <label class="mt-1 flex items-center gap-2 text-xs">
            <Switch
              checked={studentsSeeOtherStudents}
              onCheckedChange={(v) => setStudentsSeeOtherStudents(v)}
            />
            Alunos podem ver outros alunos
          </label>
        </div>
        <div class="rounded-md border border-border bg-card/30 p-3 text-[11px] text-muted-foreground">
          <div class="grid grid-cols-3 gap-2">
            <div>
              <div class="font-bold text-foreground">{company.adminCount}</div>
              <div class="text-[10px]">Admins</div>
            </div>
            <div>
              <div class="font-bold text-foreground">{company.teacherCount}</div>
              <div class="text-[10px]">Professores</div>
            </div>
            <div>
              <div class="font-bold text-foreground">{company.studentCount}</div>
              <div class="text-[10px]">Alunos</div>
            </div>
          </div>
          <div class="mt-2">Tipo: {company.isAutonomous ? "Profissional autônomo" : "Instituição"}</div>
          <div>Criada em {new Date(company.createdAt).toLocaleString()}</div>
        </div>
        <label class="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs">
          <Switch
            checked={archived}
            onCheckedChange={(v) => setArchived(v)}
          />
          <span class="font-semibold">
            {archived ? "Arquivada" : "Arquivar organização"}
          </span>
          <span class="text-[10px] text-muted-foreground">
            (memberships e bancos são preservados)
          </span>
        </label>
        {err && (
          <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {err}
          </div>
        )}
      </div>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (editing.value = null)}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          disabled={saving}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={saving || !name.trim()}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 width="1.1em" height="1.1em" className="animate-spin" />
          ) : (
            <Save width="1.1em" height="1.1em" />
          )}
          Salvar
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

// ---------- ManageMembersModal ----------
// Antigamente "Ver membros" (read-only). Agora permite adicionar usuário com
// role escolhida e revogar vínculos existentes. Backend bloqueia remoção do
// último admin pra evitar org órfã.

function ViewMembersModal({ company }: { company: CompanyDetail }) {
  // Detalhe local pra refletir add/remove sem reabrir o modal a cada vez.
  // Quando o backend retorna a versão atualizada, substituímos aqui.
  const [detail, setDetail] = useState<CompanyDetail>(company);
  const [adding, setAdding] = useState(false);
  const [pickRole, setPickRole] = useState<
    "admin_company" | "teacher" | "student"
  >("student");
  const [pickUserId, setPickUserId] = useState<string>("");
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const admins = detail.members.filter((m) => m.role === "admin_company");
  const teachers = detail.members.filter((m) => m.role === "teacher");
  const students = detail.members.filter((m) => m.role === "student");

  async function handleAdd() {
    if (!pickUserId) {
      setErr("Selecione o usuário.");
      return;
    }
    setBusy("add");
    setErr(null);
    try {
      const res = await api.addCompanyMember(detail.id, pickUserId, pickRole);
      setDetail(res.company);
      setPickUserId("");
      setAdding(false);
      // Atualiza lista principal em background pra os contadores baterem.
      load();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleRemove(m: CompanyDetail["members"][number]) {
    const roleLabel =
      m.role === "admin_company" ? "admin" : m.role === "teacher" ? "professor" : "aluno";
    if (!confirm(`Remover ${m.name} como ${roleLabel} desta organização?`)) return;
    setBusy(m.userId + ":" + m.role);
    setErr(null);
    try {
      const res = await api.removeCompanyMember(detail.id, m.userId, m.role);
      setDetail(res.company);
      load();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <ModalShell
      title={`Membros: ${detail.name}`}
      onClose={() => (viewing.value = null)}
    >
      <div class="space-y-4 p-5">
        {/* Botão de adicionar / formulário inline */}
        {!adding ? (
          <button
            type="button"
            onClick={() => setAdding(true)}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
          >
            <UserPlus width="1.1em" height="1.1em" />
            Adicionar membro
          </button>
        ) : (
          <AddMemberForm
            companyId={detail.id}
            existingMemberKeys={
              new Set(detail.members.map((m) => `${m.userId}:${m.role}`))
            }
            pickUserId={pickUserId}
            setPickUserId={setPickUserId}
            pickRole={pickRole}
            setPickRole={setPickRole}
            onCancel={() => {
              setAdding(false);
              setPickUserId("");
              setErr(null);
            }}
            onConfirm={handleAdd}
            busy={busy === "add"}
          />
        )}

        {err && (
          <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {err}
          </div>
        )}

        <MembersGroup
          label="Admins"
          icon={Shield}
          members={admins}
          onRemove={handleRemove}
          busy={busy}
        />
        <MembersGroup
          label="Professores"
          icon={GraduationCap}
          members={teachers}
          onRemove={handleRemove}
          busy={busy}
        />
        <MembersGroup
          label="Alunos"
          icon={Users}
          members={students}
          onRemove={handleRemove}
          busy={busy}
        />
      </div>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (viewing.value = null)}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
        >
          Fechar
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

function AddMemberForm({
  existingMemberKeys,
  pickUserId,
  setPickUserId,
  pickRole,
  setPickRole,
  onCancel,
  onConfirm,
  busy
}: {
  companyId: string;
  existingMemberKeys: Set<string>;
  pickUserId: string;
  setPickUserId: (v: string) => void;
  pickRole: "admin_company" | "teacher" | "student";
  setPickRole: (r: "admin_company" | "teacher" | "student") => void;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  // Filtra usuários que JÁ têm a role escolhida nesta org — evita "adicionar"
  // alguém que já está, mostrando só candidatos novos.
  const candidateExclude = useMemo(() => {
    const set = new Set<string>();
    for (const k of existingMemberKeys) {
      const [uid, role] = k.split(":");
      if (role === pickRole) set.add(uid);
    }
    return set;
  }, [existingMemberKeys, pickRole]);

  return (
    <div class="space-y-3 rounded-md border border-primary/30 bg-primary/5 p-3">
      <div class="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
        <Field label="Role">
          <div class="flex gap-1.5">
            <RolePickBtn
              label="Aluno"
              icon={Users}
              active={pickRole === "student"}
              onClick={() => setPickRole("student")}
            />
            <RolePickBtn
              label="Professor"
              icon={GraduationCap}
              active={pickRole === "teacher"}
              onClick={() => setPickRole("teacher")}
            />
            <RolePickBtn
              label="Admin"
              icon={Shield}
              active={pickRole === "admin_company"}
              onClick={() => setPickRole("admin_company")}
            />
          </div>
        </Field>
      </div>
      <Field label="Usuário">
        <UserPickerExcluding
          selected={pickUserId}
          onChange={setPickUserId}
          excludeIds={candidateExclude}
        />
      </Field>
      <div class="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy || !pickUserId}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {busy ? (
            <Loader2 width="1.1em" height="1.1em" className="animate-spin" />
          ) : (
            <UserPlus width="1.1em" height="1.1em" />
          )}
          Adicionar
        </button>
      </div>
    </div>
  );
}

function RolePickBtn({
  label,
  icon: Icon,
  active,
  onClick
}: {
  label: string;
  icon: typeof Shield;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex flex-1 items-center justify-center gap-1 rounded-md border px-2 py-1.5 text-xs font-semibold transition-colors ${
        active
          ? "border-primary/50 bg-primary/10 text-primary"
          : "border-border bg-background text-muted-foreground hover:bg-accent"
      }`}
    >
      <Icon width="1em" height="1em" />
      {label}
    </button>
  );
}

function UserPickerExcluding({
  selected,
  onChange,
  excludeIds
}: {
  selected: string;
  onChange: (id: string) => void;
  excludeIds: Set<string>;
}) {
  const [q, setQ] = useState("");
  const users = allUsers.value ?? [];
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    const candidates = users.filter((u) => !excludeIds.has(u.id));
    if (!t) return candidates.slice(0, 20);
    return candidates
      .filter((u) =>
        `${u.email} ${u.name} ${u.apelido ?? ""}`.toLowerCase().includes(t)
      )
      .slice(0, 20);
  }, [users, q, excludeIds]);
  const selUser = users.find((u) => u.id === selected);
  return (
    <div class="space-y-2">
      <div class="relative">
        <Search
          width="1em"
          height="1em"
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="search"
          value={q}
          onInput={(e) => setQ((e.target as HTMLInputElement).value)}
          placeholder="Buscar email, nome, apelido…"
          class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </div>
      <div class="max-h-[200px] overflow-y-auto rounded-md border border-border bg-background">
        {filtered.length === 0 ? (
          <p class="p-3 text-center text-xs italic text-muted-foreground">
            Nenhum usuário restante.
          </p>
        ) : (
          <ul class="divide-y divide-border">
            {filtered.map((u) => (
              <li
                key={u.id}
                class={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
                  selected === u.id ? "bg-primary/10" : "hover:bg-accent/40"
                }`}
                onClick={() => onChange(u.id)}
              >
                <input
                  type="radio"
                  checked={selected === u.id}
                  readOnly
                  aria-label={`Selecionar ${u.email}`}
                  class="size-3.5"
                />
                <div class="min-w-0 flex-1">
                  <div class="truncate font-semibold">{u.name}</div>
                  <div class="truncate font-mono text-[10px] text-muted-foreground">
                    {u.email}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {selUser && (
        <p class="text-[11px] text-muted-foreground">
          Selecionado: <span class="font-semibold">{selUser.name}</span> ({selUser.email})
        </p>
      )}
    </div>
  );
}

function MembersGroup({
  label,
  icon: Icon,
  members,
  onRemove,
  busy
}: {
  label: string;
  icon: typeof Shield;
  members: CompanyDetail["members"];
  onRemove: (m: CompanyDetail["members"][number]) => void;
  busy: string | null;
}) {
  return (
    <div>
      <div class="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        <Icon width="1em" height="1em" />
        {label}
        <span class="rounded bg-accent/60 px-1 text-[10px] tabular-nums">
          {members.length}
        </span>
      </div>
      {members.length === 0 ? (
        <p class="text-xs italic text-muted-foreground">Nenhum.</p>
      ) : (
        <ul class="divide-y divide-border rounded-md border border-border bg-card/30">
          {members.map((m) => {
            const key = m.userId + ":" + m.role;
            const isBusy = busy === key;
            return (
              <li key={key} class="flex items-center gap-2 px-3 py-1.5 text-xs">
                <div class="min-w-0 flex-1">
                  <div class="truncate font-semibold">
                    {m.name}
                    {m.apelido && (
                      <span class="ml-1 text-[10px] font-normal text-muted-foreground">
                        ({m.apelido})
                      </span>
                    )}
                  </div>
                  <div class="truncate font-mono text-[10px] text-muted-foreground">
                    {m.email}
                  </div>
                </div>
                <span class="hidden text-[10px] text-muted-foreground sm:inline">
                  desde {new Date(m.grantedAt).toLocaleDateString()}
                </span>
                <button
                  type="button"
                  onClick={() => onRemove(m)}
                  disabled={isBusy}
                  title="Remover desta organização"
                  aria-label="Remover"
                  class="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                >
                  {isBusy ? (
                    <Loader2 width="0.95em" height="0.95em" className="animate-spin" />
                  ) : (
                    <X width="0.95em" height="0.95em" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------- Helpers ----------

function Field({ label, children }: { label: string; children: any }) {
  return (
    <label class="block">
      <span class="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function ModalShell({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: any;
}) {
  // ESC fecha; click no backdrop NÃO fecha. [[feedback-modal-no-click-outside-close]]
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
    >
      <div
        class="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-border bg-background shadow-2xl"
      >
        <div class="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 class="text-base font-bold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            class="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Fechar"
          >
            <X width="1.1em" height="1.1em" />
          </button>
        </div>
        <div class="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({ children }: { children: any }) {
  return (
    <div class="flex items-center justify-end gap-2 border-t border-border bg-card/40 px-5 py-3">
      {children}
    </div>
  );
}

// === Default órfãos =====================================================
// [[project-admin-default-orphan-org]]
function OrphanDefaultPicker() {
  type Option = { id: string; slug: string; name: string };
  const [current, setCurrent] = useState<Option | null>(null);
  const [options, setOptions] = useState<Option[]>([]);
  const [loadPhase, setLoadPhase] = useState<"loading" | "ready" | "error">("loading");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  async function load() {
    setLoadPhase("loading");
    try {
      const r = await api.getOrphanDefault();
      setCurrent(r.current);
      setOptions(r.options);
      setLoadPhase("ready");
    } catch {
      setLoadPhase("error");
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function pick(slug: string) {
    if (saving) return;
    setSaving(true);
    try {
      const r = await api.setOrphanDefault(slug);
      setCurrent(r.current);
      setSavedAt(Date.now());
    } catch (err) {
      void openAlert({ title: "Erro", message: friendlyError(err) });
    } finally {
      setSaving(false);
    }
  }

  if (loadPhase === "loading") {
    return (
      <div class="flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-[12px] text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" />
        Carregando configuração de órfãos…
      </div>
    );
  }
  if (loadPhase === "error") return null;

  return (
    <section class="rounded-md border border-violet-500/30 bg-violet-500/5 px-4 py-3">
      <header class="mb-2 flex flex-wrap items-center gap-2">
        <Building2 className="size-4 text-violet-600 dark:text-violet-400" />
        <h2 class="text-[12px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300">
          Organização-padrão p/ órfãos
        </h2>
        {current && (
          <span class="ml-1 rounded-full border border-violet-500/40 bg-white px-2 py-0.5 text-[10px] font-bold text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
            Atual: {current.name}
          </span>
        )}
        {savedAt && Date.now() - savedAt < 4000 && (
          <span class="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="size-3" /> Salvo
          </span>
        )}
      </header>
      <p class="mb-2 text-[11px] leading-snug text-violet-700/80 dark:text-violet-300/80">
        Toda vez que um usuário é criado sem vínculo a outra organização, ele
        recebe automaticamente uma membership de aluno na organização escolhida
        abaixo. Fallback hardcoded: <strong>geral</strong>.
      </p>
      <label class="flex items-center gap-2 text-[12px]">
        <span class="font-semibold text-muted-foreground">Organização:</span>
        <select
          value={current?.slug ?? ""}
          onChange={(e) => void pick((e.target as HTMLSelectElement).value)}
          disabled={saving}
          class="rounded-md border border-border bg-background px-2 py-1 text-[12px] outline-none focus:border-primary disabled:opacity-50"
        >
          {options.map((o) => (
            <option key={o.id} value={o.slug}>
              {o.name} ({o.slug})
            </option>
          ))}
        </select>
        {saving && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
      </label>
    </section>
  );
}
